export const AD_SLOTS = ['TITLE', 'HOW_TO_PLAY', 'CHARACTER_SELECT', 'RESULT'] as const;
export type AdSlot = (typeof AD_SLOTS)[number];
export const AD_TIMEOUT_MS = 2_000;
export interface AdAdapter { initialize(): void | Promise<void>; show(slot: AdSlot): boolean | Promise<boolean>; hide(): void | Promise<void> }
export interface AdLogger { warn(message: string, detail?: unknown): void }
export interface AdView { show(slot: AdSlot): void; hide(): void }

export class NoOpAdAdapter implements AdAdapter {
  public initialize(): void {}
  public show(slot: AdSlot): boolean { void slot; return false; }
  public hide(): void {}
}

const withTimeout = async <T>(operation: Promise<T>, timeoutMs: number): Promise<T> => new Promise<T>((resolve, reject) => {
  const timer = globalThis.setTimeout(() => reject(new Error('Ad operation timed out.')), timeoutMs);
  void operation.then((value) => { globalThis.clearTimeout(timer); resolve(value); }, (error: unknown) => {
    globalThis.clearTimeout(timer); reject(error instanceof Error ? error : new Error(String(error)));
  });
});

export class AdService {
  private initialized = false;
  private initializationFailed = false;
  public constructor(private readonly adapter: AdAdapter, private readonly view: AdView, private readonly logger: AdLogger,
    private readonly enabled = false, private readonly timeoutMs = AD_TIMEOUT_MS) {}
  public async initialize(): Promise<void> {
    if (!this.enabled || this.initialized || this.initializationFailed) return;
    try { await withTimeout(Promise.resolve(this.adapter.initialize()), this.timeoutMs); this.initialized = true; }
    catch (error) { this.initializationFailed = true; this.logger.warn('Ad initialization failed.', error); this.view.hide(); }
  }
  public async show(slot: AdSlot): Promise<void> {
    if (!this.enabled) { this.view.hide(); return; }
    try {
      await this.initialize();
      if (this.initializationFailed) { this.view.hide(); return; }
      const shown = await withTimeout(Promise.resolve(this.adapter.show(slot)), this.timeoutMs);
      if (shown) this.view.show(slot); else this.view.hide();
    } catch (error) { this.logger.warn('Ad show failed.', error); this.view.hide(); }
  }
  public hide(): void {
    this.view.hide();
    try { void Promise.resolve(this.adapter.hide()).catch((error: unknown) => this.logger.warn('Ad hide failed.', error)); }
    catch (error) { this.logger.warn('Ad hide failed.', error); }
  }
}

export class DomAdView implements AdView {
  public constructor(private readonly element: HTMLElement) {}
  public show(slot: AdSlot): void { this.element.dataset.slot = slot; this.element.style.display = 'block'; }
  public hide(): void { delete this.element.dataset.slot; this.element.style.display = 'none'; }
}
